<template>
  <div class="checkout-step">
    <h1 class="h4 mb-4">
      <slot name="title" />
    </h1>

    <loader v-if="is.initial" />

    <b-row v-else>
      <b-col :[$root.md.key]="8" tag="article">
        <b-alert variant="danger" :show="step.isDirty && stepHasErrors">
          <h2 class="h5 alert-heading"><b-icon icon="b-exclamation-circle" /> {{ $t(`${tPath}.errors.title`) }}</h2>

          <div>
            <div v-for="(configurationProperty, pKey) in stepErrors" :key="pKey" class="error-group">
              <div v-for="(error, eIndex) in configurationProperty" :key="`${pKey}_${eIndex}`" class="error">
                {{ error[$store.getters['gui/language:get']] }}
              </div>
            </div>
          </div>
        </b-alert>

        <div>
          <slot :configuration="configuration" />
        </div>
      </b-col>

      <b-col :[$root.md.key]="4" tag="aside" :class="{ 'mt-3': !$root.isDesktop }">
        <checkout-summary>
          <template #title>
            {{ $t(`${tPath}.summary.title`) }}
          </template>

          <template #content>
            <slot name="summaryfooter" v-bind="{ basketId: basket.id, submit, isLocationNotReleased }" />
          </template>
        </checkout-summary>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { COMPONENT_CHECKOUTSUMMARY_COMPONENT_TYPE_MAP, COMPONENT_CHECKOUTSUMMARY_TYPE_COMPONENT_MAP } from '@/constants';

import Loader from '@/components/Loader';
import CheckoutSummary from '@/components/authenticated/Checkout/CheckoutSummary';

export default {
  name: 'CheckoutStep',
  components: {
    Loader,
    CheckoutSummary
  },
  props: {
    validationSections: {
      type: Array,
      default: () => ([])
    },
    initialOptionValues: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      step: {
        isInitialized: false,
        isDirty: false,
        optionRegistration: {
          timeout: null,
          debounce: 100
        },
        allowSubmit: false
      },
      optionsActive: this.initialOptionValues,
      optionsData: {}
    }
  },
  computed: {
    is() {
      return this.$store.getters['shoppingcart/is']
    },
    basket() {
      return this.$store.getters['shoppingcart/get']
    },
    configuration() {
      return this.$store.getters['shoppingcart/getConfiguration']
    },
    summary() {
      const priceDetails = this.$store.getters['shoppingcart/getSummary']
      const currentLanguage = this.$store.getters['gui/language:get']

      const mapLineTypeToType = (lineType) => {
        if (!lineType) return null
        const typeMap = {
          'Separator': 'SeparatorCosts',
          'ProductCost': 'ProductCosts',
          'NetCost': 'ProductCosts',
          'DeliveryCost': 'AdditionalCosts',
          'VatCost': 'VatCosts',
          'TotalCost': 'TotalCosts',
          'Literal': 'LiteralCosts'
        }
        return typeMap[lineType]
      }

      const priceLines = priceDetails.priceLines || []

      // Check if TotalCost already exists in priceLines
      const hasTotalCost = priceLines.some(p => {
        const type = mapLineTypeToType(p.lineType)
        return type === COMPONENT_CHECKOUTSUMMARY_TYPE_COMPONENT_MAP.total.type
      })

      const totalPriceLabel = priceDetails.totalPriceLabel
      const resolvedTotalPriceLabel = totalPriceLabel && typeof totalPriceLabel === 'object'
        ? totalPriceLabel[currentLanguage]
        : totalPriceLabel
      const hasTotalPriceLabel = !!(resolvedTotalPriceLabel && String(resolvedTotalPriceLabel).trim())

      // Only add manual total if it doesn't already exist and we can render a proper label.
      const linesToProcess = hasTotalCost
        ? priceLines
        : (hasTotalPriceLabel ? priceLines.concat({
          lineType: 'TotalCost',
          label: priceDetails.totalPriceLabel || {},
          price: priceDetails.totalPrice
        }) : priceLines)

      let hasAddedSubtotalSeparator = false

      return linesToProcess
        .map(p => {
          const type = mapLineTypeToType(p.lineType)
          const typeMapping = COMPONENT_CHECKOUTSUMMARY_COMPONENT_TYPE_MAP[type]

          // Skip if type mapping doesn't exist
          if (!typeMapping) {
            return null
          }

          return Object.assign({}, p, {
            type: type,
            component: typeMapping.component,
            attrs: typeMapping.attrs || {},
            label: (p.label && typeof p.label === 'object' ? p.label[currentLanguage] : p.label) || ''
          })
        })
        .filter(p => p !== null)
        .reduce((summary, p) => {
          // Only add separator before the first ProductCost (subtotal), not before NetCost
          if (p.type === COMPONENT_CHECKOUTSUMMARY_TYPE_COMPONENT_MAP.subtotal.type &&
            p.lineType === 'ProductCost' &&
            !hasAddedSubtotalSeparator) {
            hasAddedSubtotalSeparator = true
            return summary.concat([COMPONENT_CHECKOUTSUMMARY_TYPE_COMPONENT_MAP.separator, p])
          }
          return summary.concat(p)
        }, [])
    },
    stepOptions() {
      return Object.values(this.optionsData)
    },
    stepFormData() {
      return this.stepOptions.reduce((formData, o) => Object.assign(formData, { [o.option.name]: o.option.value }, o.formData), {})
    },
    stepValidationSections() {
      return this.validationSections.map(sKey => this.$store.getters['shoppingcart/getStepValidationResult'](sKey))
    },
    stepErrors() {
      return this.stepValidationSections.reduce((errors, v) => Object.assign(errors, v.errors), {})
    },
    stepHasErrors() {
      return Object.keys(this.stepErrors).length > 0
    },
    stepIsValid() {
      return this.stepOptions.reduce((valid, o) => o.$v.$invalid ? false : valid, true) && this.stepValidationSections.reduce((valid, s) => !s.isValid ? false : valid, true)
    },
    configurationLocationCustomerId() {
      const location = this.configuration?.location || this.configuration?.Location
      const customerId = location?.customerId || location?.CustomerId
      return typeof customerId === 'string' ? customerId.trim() : ''
    },
    profileLocations() {
      return this.$store.getters['profile/getLocations'] || {}
    },
    activeLocationId() {
      return this.$store.getters['profile/getActiveLocationId']
    },
    activeLocationCustomerId() {
      const userLocations = this.profileLocations.userLocations || []
      const activeLocation = userLocations.find((location) => String(location.locationId) === String(this.activeLocationId)) || userLocations[0] || {}
      const customerId = activeLocation.customerId || activeLocation.CustomerId
      return typeof customerId === 'string' ? customerId.trim() : ''
    },
    isThirdPartyOrder() {
      const roles = (this.$store.getters['profile/get']?.user?.roles || [])
      return roles.some(role => String(role).toLowerCase() === 'thirdpartyorder')
    },
    isLocationNotReleased() {
      if (this.isThirdPartyOrder) {
        return !this.configurationLocationCustomerId && !this.activeLocationCustomerId
      }
      return !this.activeLocationCustomerId
    }
  },
  methods: {
    validateStep() {
      this.stepOptions.forEach(g => g.$v.$touch())
      this.$emit('checkoutstep:validate')
    },
    resetStepValidation() {
      this.step.isDirty = false
      this.stepOptions.forEach(g => g.$v.$reset())
    },
    updateStep(data = {}) {
      if (this.step.optionRegistration.timeout) clearTimeout(this.step.optionRegistration.timeout)

      this.step.optionRegistration.timeout = setTimeout(() => {
        this.$set(this.optionsData, data.option.name, data)
        if (this.step.isInitialized) {
          const basketConfiguration = Object.assign({}, this.stepFormData, { orderReference: null })
          this.$store.dispatch('shoppingcart/update', basketConfiguration)
        }
        this.step.isInitialized = true
      }, this.step.optionRegistration.debounce)
    },
    submit(e) {
      this.step.isDirty = true

      if (this.isLocationNotReleased) {
        e.preventDefault()
        e.stopPropagation()
        return
      }

      if (!this.step.allowSubmit) {
        e.preventDefault()
        e.stopPropagation()

        // Get the href from the button element (Bootstrap Vue b-button with href renders as <a>)
        const getHref = () => {
          const target = e.currentTarget || e.target
          if (!target) return null

          // Check if target is an anchor
          if (target.tagName === 'A') {
            // Get absolute URL from href property, or relative from attribute
            return target.href || target.getAttribute('href')
          }

          // Check if target is inside an anchor
          const anchor = target.closest('a')
          if (anchor) {
            return anchor.href || anchor.getAttribute('href')
          }

          // Try to get href attribute from target or any parent
          let element = target
          while (element && element !== document.body) {
            const hrefAttr = element.getAttribute && element.getAttribute('href')
            if (hrefAttr) {
              // Resolve relative URLs to absolute
              return hrefAttr.startsWith('http') || hrefAttr.startsWith('/')
                ? hrefAttr
                : new URL(hrefAttr, window.location.origin).href
            }
            element = element.parentElement
          }

          return null
        }

        const href = getHref()

        // Store the button element for fallback
        const buttonElement = e.currentTarget || e.target

        this.$store.dispatch('shoppingcart/update', Object.assign({}, this.stepFormData, { orderReference: null }))
          .then((basket) => {
            this.validateStep()

            // Check validation - use response data directly if available, otherwise use store
            const validationResult = (basket && basket.configurationValidationResult) || this.$store.getters['shoppingcart/getValidationResult']
            const isApiValid = validationResult && validationResult.isAllValid === true
            const isFormValid = this.stepIsValid

            // Navigate if API validation passes (API validation is the source of truth)
            // Form validation errors will be shown but won't block navigation if API says it's valid
            if (isApiValid) {
              if (href) {
                window.location.href = href
              } else {
                // Fallback: try to find and click the anchor element
                const anchor = buttonElement.tagName === 'A' ? buttonElement : buttonElement.closest('a')
                if (anchor) {
                  this.step.allowSubmit = true
                  // Use setTimeout to ensure the click happens after the current execution
                  setTimeout(() => {
                    anchor.click()
                    this.step.allowSubmit = false
                  }, 0)
                }
              }
            }
          })
          .catch((error) => {
            console.error('Error updating basket configuration:', error)
          })
      }
    }
  },
  created() {
    this.$store.dispatch('shoppingcart/get')

    this.$on('optionform:change', this.updateStep)

    this.$on('coupon:change', () => {
      // TODO:
      // get summary infos back and update summary in aside
    })
  },
  watch: {
    configuration: {
      immediate: true,
      deep: true,
      handler() {
        Object.keys(this.initialOptionValues)
          .forEach(oKey => {
            if (!this.$root.isNullOrEmpty(this.configuration[oKey])) this.optionsActive[oKey] = this.configuration[oKey]
          })
      }
    },
    optionsActive: {
      deep: true,
      handler() {
        this.resetStepValidation()
      }
    }
  }
}
</script>

<style lang="scss">
$checkoutstep-alert-heading-border: set-nth(map-get($borders, 'base'), 3, theme-color-level('danger', $alert-border-level)) !default;
$checkoutstep-alert-heading-border-gap: $spacer * 0.5 !default;
$checkoutstep-alert-errorgroup-padding-y: $spacer * 0.5 !default;
$checkoutstep-alert-errorgroup-padding-x: 0 !default;

.checkout-step {
  @include component('y');

  .alert {
    .alert-heading {
      padding-bottom: $checkoutstep-alert-heading-border-gap;
      border-bottom: $checkoutstep-alert-heading-border;
    }

    .error-group {
      padding: $checkoutstep-alert-errorgroup-padding-y $checkoutstep-alert-errorgroup-padding-x;

      .error {}

      &:first-child {
        padding-top: 0;
      }

      &:last-child {
        padding-bottom: 0;
      }
    }
  }
}
</style>
